import { FC, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GetUsersOutput } from "server/StartupAdmins CRUD/OutputsTypes";
import { getUsers } from "server/StartupAdmins CRUD/Requests";
import { ApiResponse } from "server";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import { Box } from "@mui/material";
import { userRoleDisplay, highlightText } from "components/common/utils";
import UserDetailsModal from "./UserDetailsModal";
import { user } from "server/AccrossTypes";

const UsersListView: FC = () => {
  const navigate = useNavigate();
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [search, setSearch] = useState<string>("");
  const [users, setUsers] = useState<GetUsersOutput[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<user | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response: ApiResponse<GetUsersOutput[]> = await getUsers();
        if (response.status === 200 && Array.isArray(response.body)) {
          setError(null);
          setUsers(response.body);
        } else {
          setError("Erreur lors de la récupération des utilisateurs.");
        }
      } catch (err) {
        setError("Une erreur s'est produite lors de la connexion au serveur.");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const getFilteredUsers = () => {
    if (!users) return [];

    let filteredUsers = users;

    if (roleFilter !== "all") {
      filteredUsers = filteredUsers.filter(
        (user) => user.userRole === roleFilter,
      );
    }

    if (search) {
      filteredUsers = filteredUsers.filter((user) => {
        const name = `${user.firstname} ${user.lastname}`;
        return name.toLowerCase().includes(search.toLowerCase());
      });
    }

    return filteredUsers;
  };

  const handleViewDetails = (user: user) => {
    setSelectedUser(user);
    setShowDetailsModal(true);
  };

  const handleCloseModal = () => {
    setShowDetailsModal(false);
    setSelectedUser(null);
  };

  return (
    <div className="p-6">
      {/* Boutons d'ajout et d'annulation */}
      <div className="flex justify-between items-center mb-6">
        <button onClick={() => navigate(-1)} className="text-gray-600">
          Retour
        </button>
      </div>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Box sx={{ mt: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {!loading && !error && users && (
        <>
          <div className="mb-6 flex justify-between items-center space-x-4">
            <input
              type="text"
              placeholder="Rechercher par nom..."
              className="px-4 py-2 border rounded-md w-full"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="px-4 py-2 border rounded-md"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="all">Tous</option>
              <option value="admin">Administrateurs Startup</option>
              <option value="admin_school">Administrateurs d'école</option>
              <option value="student">Élèves</option>
              <option value="teacher">Enseignants</option>
              <option value="inspecteur">Inspecteurs</option>
              <option value="parent">Parents</option>
            </select>
          </div>

          <div className="space-y-4">
            {getFilteredUsers().length > 0 ? (
              getFilteredUsers().map((user, index) => (
                <div
                  key={user._id}
                  className="flex justify-between items-center py-4 border-b"
                >
                  <div>
                    <span className="text-gray-500 mr-2">{index + 1}.</span>
                    <span className="font-medium">
                      {highlightText(
                        user.firstname + " " + user.lastname,
                        search,
                      )}
                    </span>
                    <p className="text-gray-500 text-sm mt-1">
                      {userRoleDisplay(user.userRole)}{" "}
                      {user.schoolName ? `- ${user.schoolName}` : ""}
                      {user.userRole === "teacher"
                        ? user.availability.available
                          ? " - Disponible"
                          : " - Non disponible"
                        : ""}
                    </p>
                  </div>
                  <button
                    onClick={() => handleViewDetails(user)}
                    className="px-3 py-1 bg-[#131CC9] text-white text-sm rounded-md hover:bg-[#0f1699] transition-colors flex items-center space-x-1"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                    <span>Détails</span>
                  </button>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center">
                Aucun utilisateur trouvé.
              </p>
            )}
          </div>
        </>
      )}

      {/* Modal pour les détails de l'utilisateur */}
      <UserDetailsModal
        user={selectedUser}
        open={showDetailsModal}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default UsersListView;
